import React, { useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import Template from "./Components/Template";
import config from './config.js'
import { getCustomModules } from './Components/utils';
import { Provider, useDispatch } from "react-redux";
import store from "./store";
import { setClientLogo, setSiteClientLogo } from "./reducer";
import Login from "./Components/Auth/Login";
import ForgetPassword from './Components/Auth/ForgetPassword'
import ResetPassword from './Components/Auth/ResetPassword'
import {Scan , AssetItemList, Charts} from "./Components/CustomModules";

//Set System URL
// export let SystemUrl="https://hic.intrazero.org/";
export let SystemUrl = config.SystemUrl;
export let FrontUrl = config.FrontUrl;
export let BackendUrl = config.BackendUrl;

const DynamicRoutesBase = () => {
    const didLogRef = useRef(false);

    // TODO: If Navigation needs to update live at runtime (without full reload),
    // add a storage/event listener and a state key to recompute this memo when
    // localStorage.Navigation changes. For now we compute once on mount.
    const dynamicRoutes = useMemo(() => {
        const raw = localStorage.getItem('Navigation');
        let navArray = [];
        try {
            const parsed = raw ? JSON.parse(raw) : [];
            navArray = Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            if (!didLogRef.current) {
                console.warn('Invalid Navigation data in localStorage. Using empty routes.', e, raw);
                didLogRef.current = true;
            }
            navArray = [];
        }
        if (!didLogRef.current) {
            console.log('[Routes:init] Navigation entries:', navArray);
            didLogRef.current = true; // only log once
        }
        return navArray
            .filter((item) => Array.isArray(item) && item.length >= 4 && item[0] && item[1] && item[2] && item[3])
            .map((navItem) => (
                <Route
                    path={navItem[0]}
                    key={navItem[0]}
                    element={<Template key={navItem[0]} getmodule={navItem[1]} getaction={navItem[2]} getfolder={navItem[3]} />}
                />
            ));
    }, []);

    // TODO: Similarly, if custom modules can change during a session,
    // move getCustomModules() behind state and update deps accordingly.
    const customRoutes = useMemo(() => {
        const modules = getCustomModules();
        return modules.map((module, index) => {
            let element;
            switch (module.route) {
                case 'InventoryTaking/Scan/:it_id':
                    element = <Scan key={module.route} ComponentToRenderData={{ CurrentModule: module, SystemUrl: SystemUrl }} />;
                    break;
                case 'InventoryTaking/AssetItemList/:it_id/:room_id':
                    element = <AssetItemList key={module.route} ComponentToRenderData={{ CurrentModule: module, SystemUrl: SystemUrl }} />;
                    break;
                case 'InventoryTaking/Charts/:it_id/:room_id':
                    element = <Charts key={module.route} ComponentToRenderData={{ CurrentModule: module, SystemUrl: SystemUrl }} />;
                    break;
                default:
                    element = <Template key={module.route} getmodule={module} getaction='index' getfolder={'CustomModules/' + module.module} />;
            }
            return <Route path={'/' + module.route} key={index} element={element} />;
        });
    }, []);

    return (
        <Routes>
            <Route path='/' element={<Template key="dashboard-root" getmodule='Dashboard' getaction='index' getfolder='Dashboard' />} />
            <Route path='/dashboard' element={<Template key="dashboard" getmodule='Dashboard' getaction='index' getfolder='Dashboard' />} />
            <Route path='/formbuilder' element={<Template key="formbuilder" getmodule='FormBuilder' getaction='index' getfolder='FormBuilder' />} />
            <Route path='/Permissions/:id' element={<Template key="permissions" getmodule='Permissions' getaction='index' getfolder='Permissions' />} />
            {customRoutes}
            {dynamicRoutes}
        </Routes>
    );
};

const DynamicRoutes = React.memo(DynamicRoutesBase);

const AppInner = () => {
    console.log("App component rendered");
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    const dispatch = useDispatch();

    useEffect(() => {
        console.log("App Mounted");
        console.log("styleUrl:", localStorage.getItem('styleUrl'));
        console.log("SystemUrl:", localStorage.getItem('SystemTitle'));
        console.log("BackendUrl:", BackendUrl);

        if (!localStorage.getItem('lang')) {
            localStorage.setItem('lang', 'en')
        }
        if (!localStorage.getItem('styleUrl')) {
            localStorage.setItem('styleUrl', "/assets/css/main-modular.css")
        }
        // Clear stale logo if it was stored as a relative path (not a full URL)
        const cachedLogo = localStorage.getItem('ClientLogo');
        if (cachedLogo && !cachedLogo.startsWith('http')) {
            localStorage.removeItem('ClientLogo');
        }

        const token = localStorage.getItem('token');
        // Admin endpoint requires auth; public endpoint is at BackendUrl (no /admin/ prefix)
        const settingUrl = token
            ? SystemUrl + 'Setting/logo,title,ClientLogo'
            : BackendUrl + 'Setting/logo,title,ClientLogo';
        const headers = token ? { Authorization: 'Bearer ' + token } : {};

        axios.get(settingUrl, { headers })
            .then(result => {
                // Unwrap Laravel's nested data: { data: { data: [...] } } OR { data: [...] }
                let responseData = result.data?.data || result.data;
                if (!Array.isArray(responseData) && responseData?.data) {
                    responseData = responseData.data; // unwrap one more level
                }
                const settings = Array.isArray(responseData) ? responseData : [];

                console.log('[Setting] raw response:', responseData);
                settings.forEach(item => {
                    if (item.key === "title") {
                        localStorage.setItem('SystemTitle', item.value);
                        document.title = item.value;
                    } else if (item.key === "logo") {
                        // Prepend BackendUrl if value is a relative path (not already http/https)
                        const logoUrl = item.value && item.value.startsWith('http')
                            ? item.value
                            : BackendUrl + item.value;
                        console.log('[Setting] ClientLogo resolved URL:', logoUrl);
                        localStorage.setItem('ClientLogo', logoUrl);
                        dispatch(setClientLogo(logoUrl));
                    } else if (item.key === "ClientLogo" && item.value) {
                        const clientLogoUrl = item.value.startsWith('http')
                            ? item.value
                            : BackendUrl + item.value;
                        localStorage.setItem('SiteClientLogo', clientLogoUrl);
                        dispatch(setSiteClientLogo(clientLogoUrl));
                    }
                });
            })
            .catch((error) => {
                console.warn('Failed to fetch system settings:', error.response?.status, error.message);
            });
    }, [dispatch])
    
    return (
        <div className="App">
            <Router>
                {isLoggedIn ? (
                    <DynamicRoutes />
                ) : (
                    <Routes>
                        <Route path='/reset-password/:token' element={<ResetPassword />} />
                        <Route path='/forget-password' element={<ForgetPassword />} />
                        <Route path='/*' element={<Login />} />
                    </Routes>
                )}
            </Router>
        </div>
    );
}

const App = () => (
    <Provider store={store}>
        <AppInner />
    </Provider>
);

export default App;
